import { ListMagnifyingGlassIcon } from '@phosphor-icons/react'
import { type Tool, type TriageCounts } from '../../utils/api-client/api-schemas'
import { TooltipWithHover } from '../tooltip'
import { type TriageSegment, mapTriageToLabels, mapTriageToSegments } from './composition-utils'
import { SegmentsBar, type SegmentsBarSize } from './segments-bar'
import * as barStyles from './segments-bar.css'
import { NoAnalysisCircle, SegmentsLabels } from './segments-labels'
import * as labelStyles from './segments-labels.css'

type TriageCompositionBarProps = {
  statistics: TriageCounts
  total: number
  tool: Tool
  size?: SegmentsBarSize
  isTransitioning?: boolean
  onSegmentClick?: (label: TriageSegment) => unknown
  selectedSegments?: Iterable<TriageSegment>
  nonClickableSegments?: TriageSegment[]
  visibleSegments?: TriageSegment[]
}

export const TriageCompositionBar: React.FC<TriageCompositionBarProps> = ({
  statistics,
  total,
  tool,
  size = 'tiny',
  isTransitioning,
  onSegmentClick,
  selectedSegments = new Set<TriageSegment>(),
  nonClickableSegments = [],
  visibleSegments,
}) => {
  const selectedSegmentsSet = selectedSegments instanceof Set ? selectedSegments : new Set(selectedSegments)
  const allSegments = mapTriageToSegments(statistics, total, tool)

  if (isTransitioning) {
    const skeletonSegments = allSegments.map(s => ({
      ariaLabel: s.ariaLabel,
      percentage: s.percentage,
      color: '',
      className: barStyles.skeletonSegment,
    }))
    return <SegmentsBar segments={skeletonSegments} size={size} />
  }

  const allLabels = mapTriageToLabels(statistics, total, tool).map(label =>
    label.name === 'Not triaged' ? { ...label, icon: <NoAnalysisCircle /> } : label
  )
  const labels = visibleSegments ? allLabels.filter(l => visibleSegments.includes(l.name)) : allLabels

  const hasActiveFilters = selectedSegmentsSet.size > 0
  const isHiddenByQuickView = (ariaLabel: string) =>
    visibleSegments ? !visibleSegments.includes(ariaLabel as TriageSegment) : false
  const segments = allSegments.map(segment => ({
    ...segment,
    pressed: selectedSegmentsSet.has(segment.ariaLabel),
    dimmed: isHiddenByQuickView(segment.ariaLabel) || (hasActiveFilters && !selectedSegmentsSet.has(segment.ariaLabel)),
    onClick:
      onSegmentClick && !nonClickableSegments.includes(segment.ariaLabel) && !isHiddenByQuickView(segment.ariaLabel)
        ? () => onSegmentClick(segment.ariaLabel)
        : undefined,
  }))

  return (
    <TooltipWithHover
      trigger={<SegmentsBar segments={segments} size={size} />}
      maxWidth={1200}
      side="bottom"
      sideOffset={8}
      bare
    >
      <SegmentsLabels
        labels={labels}
        icon={<ListMagnifyingGlassIcon className={labelStyles.leadingIconSvg} weight="light" />}
      />
    </TooltipWithHover>
  )
}
